import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { secure, withParams } from 'utils/client';
import { toFormData } from 'utils/helpers';
import { updateRecentlyViewedStack } from '../utils';
import { types, tags, LIST } from '../consts';

const {
  REACT_APP_API_URL: baseUrl,
  REACT_APP_STRAPI_API_URL: strapiUrl,
  REACT_APP_STRAPI_KEY: strapiKey,
} = process.env;

const fromStrapi = (query) => {
  return {
    ...query,
    url: `${strapiUrl}${query.url}`,
    headers: {
      Authorization: `Bearer ${strapiKey}`,
      'Content-type': 'application/json',
      ...query.headers,
    },
  };
};

const api = createApi({
  tagTypes: Object.values(types),
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (build) => ({
    // AUTHORIZATION
    ...{
      // POST
      authorizeCustomer: build.mutation({
        query: ({ body }) => ({
          method: 'POST',
          url: '/auth',
          body: toFormData(body),
        }),
      }),
    },

    // CATEGORIES
    ...{
      // [GET] /categories/flat-tree
      getCategoriesFlatTree: build.query({
        query: () => ({
          method: 'GET',
          url: '/categories/flat-tree',
        }),
        providesTags: [tags.CATEGORIES_FLAT_TREE()],
      }),

      // [GET] /categories/tree
      getCategoriesTree: build.query({
        query: () => ({
          method: 'GET',
          url: '/categories/tree',
        }),
        providesTags: [tags.CATEGORIES_TREE()],
      }),
    },

    // SEARCH
    ...{
      // [POST] /search/products
      searchProducts: build.query({
        query: ({ body }) =>
          secure({
            method: 'POST',
            url: '/search/products',
            body,
          }),
        providesTags: [tags.SEARCH_PRODUCTS(LIST)],
      }),

      // [POST] /search/lab_groups/members
      searchMembersFromCustomerGroups: build.query({
        query: ({ body }) =>
          secure({
            method: 'POST',
            url: '/search/lab_groups/members',
            body,
          }),
        providesTags: [tags.LAB_GROUP_MEMBERS_SEARCH(LIST)],
      }),
    },

    // PRODUCTS
    ...{
      // [GET] /products/by-slug/:slug
      getProductBySlug: build.query({
        query: ({ slug, supplier_code }) =>
          secure({
            method: 'GET',
            url: withParams(`/products/by-slug/${slug}`, { supplier_code }),
          }),
        providesTags: (res, error, { slug }) => [tags.PRODUCT(slug)],
        transformResponse: (
          response,
          query,
          { slug, ignoreRecentlyViewed },
        ) => {
          if (!ignoreRecentlyViewed) {
            updateRecentlyViewedStack(response?.id);
          }
          return response;
        },
      }),

      // [GET] /products/{product_id}/reviews
      getProductReviews: build.query({
        query: ({ product_id, params }) => ({
          method: 'GET',
          url: withParams(`/products/${product_id}/reviews`, params),
        }),
        providesTags: (res, error, { id }) => [tags.PRODUCT_REVIEWS(id)],
      }),

      // [POST] /products/{product_id}/reviews
      addReviewForProduct: build.mutation({
        query: ({ product_id, body }) =>
          secure({
            method: 'POST',
            url: `/products/${product_id}/reviews`,
            body,
          }),
        invalidatesTags: (result, error, { product_id }) => [
          tags.PRODUCT(product_id),
          tags.PRODUCT_REVIEWS(product_id),
        ],
      }),

      getProductCitations: build.query({
        query: ({ product_id, params }) => ({
          method: 'GET',
          url: withParams(`/products/${product_id}/citations`, params),
        }),
        providesTags: (res, error, { product_id }) => [
          tags.PRODUCT_CITATIONS(product_id),
        ],
      }),

      // [GET] /products/available-suppliers/{product_id}
      getProductAvailableSuppliers: build.query({
        query: ({ product_id }) => ({
          method: 'GET',
          url: `/products/available-suppliers/${product_id}`,
        }),
        providesTags: (res, error, { product_id }) => [
          tags.PRODUCT_AVAILABLE_SUPPLIERS(product_id),
        ],
      }),

      // [GET] /products/{product_id}/accessory
      getProductAccessories: build.query({
        query: ({ product_id }) => ({
          method: 'GET',
          url: `/products/${product_id}/accessory`,
        }),
        providesTags: (res, error, { product_id }) => [
          tags.PRODUCT_ACCESSORIES(product_id),
        ],
      }),

      // [GET] /products/{product_id}/related
      getProductRelatedItems: build.query({
        query: ({ product_id }) =>
          secure({
            method: 'GET',
            url: `/products/${product_id}/related`,
          }),
        providesTags: (result, error, { product_id }) => [
          tags.PRODUCT_RELATED_ITEMS(product_id),
        ],
      }),

      // [GET] /products/{product_id}/questions
      getProductQuestions: build.query({
        query: ({ product_id, params }) => ({
          method: 'GET',
          url: withParams(`/products/${product_id}/questions`, params),
        }),
        providesTags: (res, error, { product_id }) => [
          tags.PRODUCT_QUESTIONS(product_id),
        ],
      }),

      // [POST] /products/{product_id}/questions
      addProductQuestion: build.mutation({
        query: ({ product_id, body }) =>
          secure({
            method: 'POST',
            url: `/products/${product_id}/questions`,
            body,
          }),
        invalidatesTags: (result, error, { product_id }) => [
          tags.PRODUCT_QUESTIONS(product_id),
        ],
      }),

      // [GET] /products/favorites
      getFavoriteProducts: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/products/favorites',
          }),
        providesTags: [tags.FAVORITE_PRODUCTS(LIST)],
      }),

      // [GET] /products/{product_id}/family
      getFamilyProducts: build.query({
        query: ({ product_id }) =>
          secure({
            method: 'GET',
            url: `/products/${product_id}/family`,
          }),
        providesTags: (result, error, { product_id }) => [
          tags.PRODUCT_FAMILY(product_id),
        ],
      }),
    },

    // CUSTOMERS
    ...{
      // [POST] /customers/reset-password
      resetPasswordRequest: build.mutation({
        query: ({ body }) => ({
          method: 'POST',
          url: '/customers/reset-password',
          body,
        }),
      }),

      // [POST] /customers/reset-password/verify-hash
      verifyResetPasswordHash: build.mutation({
        query: ({ body }) => ({
          method: 'POST',
          url: '/customers/reset-password/verify-hash',
          body,
        }),
      }),

      // [POST] /customers/update-password
      updatePassword: build.mutation({
        query: ({ body }) => ({
          method: 'POST',
          url: '/customers/update-password',
          body,
        }),
      }),

      // [GET] /customers/me/bookmarks
      getListOfCustomerBookmarks: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/customers/me/bookmarks',
          }),
        providesTags: [tags.CUSTOMER_BOOKMARKS(LIST)],
      }),

      // [POST] /customers/me/bookmarks
      addProductToBookmarks: build.mutation({
        query: ({ body }) =>
          secure({
            method: 'POST',
            url: '/customers/me/bookmarks',
            body,
          }),
        invalidatesTags: [tags.CUSTOMER_BOOKMARKS(LIST)],
      }),

      // [DELETE] /customers/me/bookmarks/{bookmark_id}
      deleteCustomerBookmark: build.mutation({
        query: ({ bookmark_id }) =>
          secure({
            method: 'DELETE',
            url: `/customers/me/bookmarks/${bookmark_id}`,
          }),
        invalidatesTags: [tags.CUSTOMER_BOOKMARKS(LIST)],
      }),

      // [GET] /customers/me/address
      getCustomerAddresses: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/customers/me/address',
          }),
        providesTags: [tags.CUSTOMER_ADDRESS(LIST)],
      }),

      // [POST] /customers/me/address
      addCustomerAddress: build.mutation({
        query: ({ body }) =>
          secure({
            method: 'POST',
            url: '/customers/me/address',
            body,
          }),
        invalidatesTags: (result, error) => {
          if (error) {
            return [];
          }
          return [tags.CUSTOMER_ADDRESS(LIST)];
        },
      }),

      // [DELETE] /customers/me/address/{address_id}
      deleteCustomerAddress: build.mutation({
        query: ({ address_id }) =>
          secure({
            method: 'DELETE',
            url: `/customers/me/address/${address_id}`,
          }),
        invalidatesTags: [tags.CUSTOMER_ADDRESS(LIST)],
      }),

      // [PATCH] /customers/me/address/{address_id}
      updateCustomerAddress: build.mutation({
        query: ({ address_id, body }) =>
          secure({
            method: 'PATCH',
            url: `/customers/me/address/${address_id}`,
            body,
          }),
        invalidatesTags: [tags.CUSTOMER_ADDRESS(LIST)],
      }),

      // [GET] /customers/me/carts
      getCustomerCarts: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/customers/me/carts',
          }),
        providesTags: [tags.CUSTOMER_CART(LIST)],
      }),

      // [GET] /me
      getCustomerDetails: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/me',
          }),
        providesTags: [tags.ME()],
      }),

      updateCustomerDetails: build.mutation({
        query: (body) =>
          secure({
            method: 'PATCH',
            url: '/me/personal-information',
            body,
          }),
        invalidatesTags: [tags.ME()],
      }),

      // [GET] /me/payment_methods
      getListOfCustomerPaymentMethods: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/me/payment_methods',
          }),
        providesTags: [tags.PAYMENT_METHODS(LIST)],
      }),

      // [POST] /me/payment_methods
      addCustomerPaymentMethod: build.mutation({
        query: ({ body }) =>
          secure({
            method: 'POST',
            url: '/me/payment_methods',
            body,
          }),
        invalidatesTags: [tags.PAYMENT_METHODS(LIST)],
      }),

      // [DELETE] /me/payment_methods/{payment_method_id}
      deleteCustomerPaymentMethod: build.mutation({
        query: ({ payment_method_id }) =>
          secure({
            method: 'DELETE',
            url: `/me/payment_methods/${payment_method_id}`,
          }),
        invalidatesTags: [tags.PAYMENT_METHODS(LIST)],
      }),

      // [GET] /me/payment_methods/po
      getListOfPurchaseOrders: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/me/payment_methods/po',
          }),
        providesTags: [tags.PURCHASE_ORDERS(LIST)],
      }),

      // [POST] /me/payment_methods/po
      createCustomerPurchaseOrder: build.mutation({
        query: ({ body }) =>
          secure({
            method: 'POST',
            url: '/me/payment_methods/po',
            body,
          }),
        invalidatesTags: [tags.PURCHASE_ORDERS(LIST)],
      }),

      // [DELETE] /me/payment_methods/po/{purchase_order_id}
      deleteCustomerPurchaseOrder: build.mutation({
        query: ({ purchase_order_id }) =>
          secure({
            method: 'DELETE',
            url: `/me/payment_methods/po/${purchase_order_id}`,
          }),
        invalidatesTags: [tags.PURCHASE_ORDERS(LIST)],
      }),

      // [PATCH] /me/orders/{order_id}/cancel
      cancelOrder: build.mutation({
        query: ({ order_id }) =>
          secure({
            method: 'PATCH',
            url: `/me/orders/${order_id}/cancel`,
          }),
        invalidatesTags: (result, error, { order_id }) => [
          tags.ORDER(LIST),
          tags.ORDER(order_id),
        ],
      }),

      // [PATCH] /me/orders/{order_id}/items/{order_item_id}/cancel
      cancelOrderItem: build.mutation({
        query: ({ order_id, order_item_id }) =>
          secure({
            method: 'PATCH',
            url: `/me/orders/${order_id}/items/${order_item_id}/cancel`,
          }),
        invalidatesTags: (result, error, { order_id }) => [
          tags.ORDER(LIST),
          tags.ORDER(order_id),
        ],
      }),

      // [POST] /me/orders/{order_id}/tags
      tagMember: build.mutation({
        query: ({ order_id, body }) =>
          secure({
            method: 'POST',
            url: `/me/orders/${order_id}/tags`,
            body,
          }),
        invalidatesTags: (result, error, { order_id }) => [
          tags.ORDER(LIST),
          tags.ORDER(order_id),
        ],
      }),

      // [DELETE] /me/orders/{order_id}/tags/{member_id}
      untagMember: build.mutation({
        query: ({ order_id, member_id }) =>
          secure({
            method: 'DELETE',
            url: `/me/orders/${order_id}/tags/${member_id}`,
          }),
        invalidatesTags: (result, error, { order_id }) => [
          tags.ORDER(order_id),
        ],
      }),

      // [GET] /me/orders
      getListOfCustomerOrders: build.query({
        query: ({ params }) =>
          secure({
            method: 'GET',
            url: withParams('/me/orders', params),
          }),
        providesTags: [tags.ORDER(LIST)],
      }),

      // [GET] /me/orders/archive
      getListOfArchivedCustomerOrders: build.query({
        query: ({ params }) =>
          secure({
            method: 'GET',
            url: withParams('/me/orders/archive', params),
          }),
        providesTags: [tags.ORDER(LIST)],
      }),

      // [GET] /me/recently_viewed_products
      getRecentlyViewedProducts: build.mutation({
        query: (product_ids) =>
          secure({
            method: 'GET',
            url: withParams('/me/recently_viewed_products', { product_ids }),
          }),
      }),
    },

    // SHOPPING CART
    ...{
      // [GET] /cart/{cart_id}
      getShoppingCartDetails: build.query({
        query: ({ cart_id }) =>
          secure({
            method: 'GET',
            url: `/cart/${cart_id}`,
          }),
        providesTags: (result, error, { cart_id }) => [
          tags.CUSTOMER_CART(cart_id),
        ],
      }),

      // [POST] /cart/{cart_id}
      addProductToCart: build.mutation({
        query: ({ cart_id, body }) =>
          secure({
            method: 'POST',
            url: `/cart/${cart_id}`,
            body,
          }),
        invalidatesTags: (result, error, { cart_id }) => [
          tags.CUSTOMER_CART(LIST),
          tags.CUSTOMER_CART(cart_id),
          tags.CART_SUMMARY(cart_id),
          tags.CART_PREVIEW(cart_id),
          tags.CART_PREVIEW(cart_id),
        ],
      }),

      // [DELETE] /cart/{cart_id}/item/{item_id}
      removeProductFromCart: build.mutation({
        query: ({ cart_id, item_id }) =>
          secure({
            method: 'DELETE',
            url: `/cart/${cart_id}/item/${item_id}`,
          }),
        invalidatesTags: (result, error, { cart_id }) => [
          tags.CUSTOMER_CART(LIST),
          tags.CUSTOMER_CART(cart_id),
          tags.CART_SUMMARY(cart_id),
          tags.CART_PREVIEW(cart_id),
        ],
      }),

      // [PATCH] /cart/{cart_id}/item/{item_id}
      updateProductInCart: build.mutation({
        query: ({ cart_id, item_id, body }) =>
          secure({
            method: 'PATCH',
            url: `/cart/${cart_id}/item/${item_id}`,
            body,
          }),
        invalidatesTags: (result, error, { cart_id }) => [
          tags.CUSTOMER_CART(LIST),
          tags.CUSTOMER_CART(cart_id),
          tags.CART_SUMMARY(cart_id),
          tags.CART_PREVIEW(cart_id),
        ],
        onQueryStarted: async (
          { cart_id, item_id, body },
          { dispatch, queryFulfilled },
        ) => {
          const optimisticPatch = dispatch(
            api.util.updateQueryData(
              api.endpoints.getShoppingCartDetails.name,
              { cart_id },
              // When request has made, don't wait for success, update quantity immediately
              (draftCartDetails) => {
                draftCartDetails.items.forEach((item) => {
                  if (item.id === item_id) {
                    item.qty = body.qty;
                  }
                });
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch (ex) {
            // Undo cache update if request was failed
            optimisticPatch.undo();
          }
        },
      }),

      // [PATCH] /cart/{cart_id}/item/{item_id}/accept
      acceptItemInSharedCart: build.mutation({
        query: ({ cart_id, item_id }) =>
          secure({
            method: 'PATCH',
            url: `/cart/${cart_id}/item/${item_id}/accept`,
          }),
        invalidatesTags: (result, error, { cart_id }) => [
          tags.CUSTOMER_CART(cart_id),
          tags.CUSTOMER_CART(LIST),
          tags.CART_PREVIEW(cart_id),
        ],
      }),

      // [PATCH] /cart/{cart_id}/item/{item_id}/reject
      rejectItemInSharedCart: build.mutation({
        query: ({ cart_id, item_id }) =>
          secure({
            method: 'PATCH',
            url: `/cart/${cart_id}/item/${item_id}/reject`,
          }),
        invalidatesTags: (result, error, { cart_id }) => [
          tags.CUSTOMER_CART(cart_id),
          tags.CUSTOMER_CART(LIST),
          tags.CART_PREVIEW(cart_id),
        ],
      }),

      // [GET] /cart/{cart_id}/preview
      getPreviewShoppingCart: build.query({
        query: ({ cart_id }) =>
          secure({
            method: 'GET',
            url: `/cart/${cart_id}/preview`,
          }),
        providesTags: (result, error, { cart_id }) => [
          tags.CART_PREVIEW(cart_id),
        ],
      }),

      // [POST] /cart/{cart_id}/summary
      getShoppingCartSummary: build.query({
        query: ({ cart_id, body = {} }) =>
          secure({
            method: 'POST',
            url: `/cart/${cart_id}/summary`,
            body,
          }),
        providesTags: (result, error, { cart_id }) => [
          tags.CART_SUMMARY(cart_id),
        ],
      }),

      // [POST] /cart/{cart_id}/promo_codes/{promo_code}/apply
      applyPromoCode: build.mutation({
        query: ({ cart_id, promo_code }) =>
          secure({
            method: 'POST',
            url: `/cart/${cart_id}/promo_codes/${promo_code}/apply`,
          }),
        invalidatesTags: (result, error, { cart_id }) => [
          tags.CART_SUMMARY(cart_id),
          tags.CART_PREVIEW(cart_id),
          tags.CUSTOMER_CART(cart_id),
        ],
      }),

      // [POST] /cart/{cart_id}/promo_codes/{promo_code}/cancel
      cancelPromoCode: build.mutation({
        query: ({ cart_id, promo_code }) =>
          secure({
            method: 'POST',
            url: `/cart/${cart_id}/promo_codes/${promo_code}/cancel`,
          }),
        invalidatesTags: (result, error, { cart_id }) => [
          tags.CART_SUMMARY(cart_id),
          tags.CART_PREVIEW(cart_id),
          tags.CUSTOMER_CART(cart_id),
        ],
      }),
    },

    // ORDERS
    ...{
      // [POST] /orders
      placeOrder: build.mutation({
        query: ({ body }) =>
          secure({
            method: 'POST',
            url: '/orders',
            body,
          }),
        invalidatesTags: [
          tags.ORDER(LIST),
          tags.CUSTOMER_CART(),
          tags.CART_PREVIEW(),
          tags.CART_SUMMARY(),
          tags.ORDER_PLACED(),
        ],
      }),

      // [GET] /orders/{order_id}
      getPlacedOrderDetails: build.query({
        query: ({ order_id }) =>
          secure({
            method: 'GET',
            url: `/orders/${order_id}`,
          }),
        providesTags: (result, error, { order_id }) => [
          tags.ORDER_PLACED(order_id),
        ],
      }),

      // [GET] /orders/{order_id}/shipments_status
      getOrderShipmentStatuses: build.query({
        query: ({ order_id }) =>
          secure({
            method: 'GET',
            url: `/orders/${order_id}/shipments_status`,
          }),
        providesTags: (result, error, { order_id }) => [
          tags.ORDER_SHIPMENT_STATUS(order_id),
        ],
      }),

      // [GET] /me/orders/{order_id}
      getOrderDetails: build.query({
        query: ({ order_id }) =>
          secure({
            method: 'GET',
            url: `/me/orders/${order_id}`,
          }),
        providesTags: (result, error, { order_id }) => [tags.ORDER(order_id)],
      }),

      // [GET] /me/orders/{order_id}/calculators
      getOrderCalculators: build.query({
        query: ({ order_id }) =>
          secure({
            method: 'GET',
            url: `/me/orders/${order_id}/calculators`,
          }),
        providesTags: (result, error, { order_id }) => [
          tags.ORDER_CALCULATOR(order_id),
        ],
      }),

      // [PUT] /me/orders/{order_id}/payment_methods
      changeOrderPaymentMethod: build.mutation({
        query: ({ order_id, body }) =>
          secure({
            method: 'GET',
            url: `/me/orders/${order_id}/payment_methods`,
            body,
          }),
        invalidatesTags: (result, error, { order_id }) => [
          tags.ORDER(order_id),
        ],
      }),
    },

    // REVIEWS
    ...{
      // [POST] /reviews/{review_id}/vote
      voteForReview: build.mutation({
        query: ({ review_id, body }) =>
          secure({
            method: 'POST',
            url: `/reviews/${review_id}/vote`,
            body,
          }),
        invalidatesTags: (result, error, { review_id }) => [
          tags.PRODUCT_REVIEWS(),
        ],
      }),

      // [GET] /reviews/{review_id}/comments
      getCommentsForReview: build.query({
        query: ({ review_id }) => ({
          method: 'GET',
          url: `/reviews/${review_id}/comments`,
        }),
        providesTags: (result, error, { review_id }) => [
          tags.REVIEW_COMMENTS(review_id),
        ],
      }),

      // [POST] /reviews/{review_id}/reports
      reportReview: build.mutation({
        query: ({ review_id, body }) =>
          secure({
            method: 'POST',
            url: `/reviews/${review_id}/reports`,
            body,
          }),
      }),
    },

    // QUOTES
    ...{
      // [POST] /me/quotes
      createQuoteRequest: build.mutation({
        query: ({ body }) =>
          secure({
            method: 'POST',
            url: '/me/quotes',
            body,
          }),
        invalidatesTags: [tags.QUOTES(LIST)],
      }),

      // [POST] /me/quotes/generate
      generateQuoteRequest: build.mutation({
        query: ({ body }) =>
          secure({
            method: 'POST',
            url: '/me/quotes/generate',
            body,
            responseHandler: async (response) => {
              const url = window.URL.createObjectURL(await response.blob());
              return url;
            },
          }),
        invalidatesTags: [tags.QUOTES(LIST)],
      }),

      // [POST] /me/quotes/{quote_id}/checkout
      checkoutQuote: build.mutation({
        query: ({ quote_id }) =>
          secure({
            method: 'POST',
            url: `/me/quotes/${quote_id}/checkout`,
          }),
        invalidatesTags: [tags.QUOTES(LIST)],
      }),
    },

    // DATA
    ...{
      // [GET] /data/countries
      getListOfCountries: build.query({
        query: () => ({
          method: 'GET',
          url: '/data/countries',
        }),
        providesTags: [tags.COUNTRIES(LIST)],
      }),
    },

    // SUPPLIER
    ...{
      // [GET] /suppliers
      getListOfSuppliers: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/suppliers',
          }),
        providesTags: [tags.SUPPLIERS(LIST)],
      }),

      // [GET] /suppliers/setup-form/{form_id}
      getSupplierSetupFormDetails: build.query({
        query: ({ form_id }) => ({
          method: 'GET',
          url: `/suppliers/setup-form/${form_id}`,
        }),
        providesTags: (result, error, { form_id }) => [
          tags.SETUP_FORM(form_id),
        ],
      }),

      // [PATCH] /suppliers/setup-form/{form_id}
      updateSupplierSetupForm: build.mutation({
        query: ({ form_id, body }) => ({
          method: 'PATCH',
          url: `/suppliers/setup-form/${form_id}`,
          body,
        }),
        invalidatesTags: (result, error, { form_id }) => [
          tags.SETUP_FORM(form_id),
        ],
      }),

      // [DELETE] /suppliers/setup-form/{form_id}/files/{file_type}
      deleteSetupFormFile: build.mutation({
        query: ({ form_id, file_type }) => ({
          method: 'DELETE',
          url: `/suppliers/setup-form/${form_id}/files/${file_type}`,
        }),
        invalidatesTags: (result, error, { form_id }) => [
          tags.SETUP_FORM(form_id),
        ],
      }),

      // [GET] /suppliers
      getListOfSupplierPartners: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/supplier-partners',
          }),
        providesTags: [tags.SUPPLIER_PARTNERS(LIST)],
      }),
    },

    // CONTENT
    ...{
      // [GET] /content/blocks
      getBlocks: build.query({
        query: ({ params }) => ({
          method: 'GET',
          url: withParams('/content/blocks', params),
        }),
        providesTags: [tags.BLOCKS(LIST)],
      }),

      getNews: build.query({
        query: ({ params }) => ({
          method: 'GET',
          url: withParams('/content/news', params),
        }),
        providesTags: [tags.NEWS(LIST)],
      }),

      getNewsCategories: build.query({
        query: ({ params }) => ({
          method: 'GET',
          url: withParams('/content/news_categories', params),
        }),
        providesTags: [tags.NEWS_CATEGORIES(LIST)],
      }),

      getNewsArticle: build.query({
        query: ({ params, query }) => ({
          method: 'GET',
          url: withParams(`/content/news/${query.slug}`, params),
        }),
        providesTags: (res, err, { query }) => [
          tags.NEWS_CATEGORIES(query.slug),
        ],
      }),
    },

    // PUNCHOUT
    ...{
      // [POST] /punchout/auth_by_key
      punchoutAuthentication: build.mutation({
        query: ({ body }) => ({
          method: 'POST',
          url: '/punchout/auth_by_key',
          body,
        }),
      }),
    },

    // STRAPI
    ...{
      getCareerArticleDetails: build.query({
        query: ({ slug }) =>
          fromStrapi({
            method: 'GET',
            url: `/career-articles?filters[slug][$eq]=${slug}&populate=*`,
          }),
        providesTags: (result, error, { slug }) => [
          tags.STRAPI_CAREER_ARTICLE(slug),
        ],
      }),

      getBlogArticles: build.query({
        query: ({ params }) => ({
          method: 'GET',
          url: withParams('/blog/articles', params),
        }),
        providesTags: [tags.BLOG(LIST)],
      }),

      getBlogArticleDetails: build.query({
        query: ({ slug }) => ({
          method: 'GET',
          url: `/blog/articles/${slug}`,
        }),
        providesTags: (result, error, { slug }) => [tags.BLOG(slug)],
      }),

      getBlogCategories: build.query({
        query: () => ({
          method: 'GET',
          url: '/blog/categories',
        }),
        providesTags: [tags.BLOG_CATEGORIES(LIST)],
      }),

      putBlogArticleViewCounter: build.mutation({
        query: ({ id, body }) =>
          fromStrapi({
            method: 'PUT',
            url: `/articles/${id}`,
            body,
          }),
      }),

      postJoinOurList: build.mutation({
        query: (body) => ({
          method: 'POST',
          url: `/mailing/join_our_list`,
          body,
        }),
      }),

      getCareersVacancies: build.query({
        query: ({ params }) => ({
          method: 'GET',
          url: withParams('/career/vacancies', params),
        }),
        providesTags: [tags.CAREERS(LIST)],
      }),

      getCareersVacancyDetails: build.query({
        query: ({ slug }) => ({
          method: 'GET',
          url: `/career/vacancies/${slug}`,
        }),
        providesTags: (result, error, { slug }) => [tags.CAREERS_VACANCY(slug)],
      }),

      getCareersVacancyCategories: build.query({
        query: () => ({
          method: 'GET',
          url: '/career/categories',
        }),
        providesTags: [tags.CAREERS_CATEGORIES(LIST)],
      }),

      getHelpCenterArticles: build.query({
        query: ({ params }) => ({
          method: 'GET',
          url: withParams('/help_center/articles', params),
        }),
        providesTags: [tags.HELP_CENTER_ARTICLES(LIST)],
      }),

      getHelpCenterArticleDetails: build.query({
        query: ({ slug }) => ({
          method: 'GET',
          url: `/help_center/articles/${slug}`,
        }),
        providesTags: (result, error, { slug }) => [
          tags.HELP_CENTER_ARTICLES(slug),
        ],
      }),

      getHelpCenterCategories: build.query({
        query: () => ({
          method: 'GET',
          url: '/help_center/categories',
        }),
        providesTags: [tags.HELP_CENTER_CATEGORIES(LIST)],
      }),

      updateHelpCenterHelpful: build.mutation({
        query: ({ id, question_is_useful }) => ({
          method: 'PATCH',
          url: `/help_center/articles/${id}/votes?question_is_useful=${question_is_useful}`,
        }),
        invalidatesTags: (result, error, { slug }) => [
          tags.HELP_CENTER_ARTICLES(slug),
        ],
      }),
    },

    // Support
    ...{},

    // Documents
    ...{
      getDocuments: build.query({
        query: () =>
          secure({
            method: 'GET',
            url: '/me/documents',
          }),
        providesTags: [tags.DOCUMENTS(LIST)],
      }),

      postDocument: build.mutation({
        query: ({ category_id, fileId }) =>
          secure({
            method: 'POST',
            url: `/me/documents/${category_id}`,
            body: {
              temp_file_id: fileId,
            },
          }),
        invalidatesTags: [tags.DOCUMENTS(LIST)],
      }),

      deleteDocument: build.mutation({
        query: ({ document_id }) =>
          secure({
            method: 'DELETE',
            url: `/me/documents/${document_id}`,
          }),
        invalidatesTags: (result, error, { document_id }) => [
          tags.DOCUMENTS(LIST),
          tags.DOCUMENTS(document_id),
        ],
      }),

      getDocument: build.mutation({
        query: ({ document_id }) =>
          secure({
            method: 'GET',
            url: `me/documents/${document_id}`,
          }),
        providesTags: (result, error, { document_id }) => [
          tags.DOCUMENTS(document_id),
        ],
      }),

      // Personal information
    },
  }),
});

export default api;
